import React from 'react';
import { Tooltip, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import VisitTab from './components/VisitTab';
import DocumentsTab from './components/DocumentsTab';
import TasksTab from './components/TasksTab';
import GlobalLoader from '../../../components/GlobalLoader';
import { useAddEditViewController } from './hooks';

const AddEditView: React.FC = () => {
  const { t } = useTranslation();
  const controller = useAddEditViewController();

  if (controller.visitLoading && !controller.isAddMode) {
    return (
      <div className="d-flex justify-content-center py-6">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  const tabsDisabledTooltip = controller.tabsDisabled
    ? t('propertyVisits.addEditView.pageTexts.tabs.disabledTooltip')
    : '';

  return (
    <>
      {controller.docLoading && <GlobalLoader />}
      <Header isAddMode={controller.isAddMode} />

      <div className="card mb-6">
        {/* Tab navigation */}
        <div className="card-header overflow-hidden no-padding">
          <Tabs
            value={controller.activeTab}
            onChange={(_, v) => controller.setActiveTab(v)}
            textColor="primary"
            className="pt-2"
            variant="fullWidth"
          >
            <Tab
              value="visit"
              label={t('propertyVisits.addEditView.pageTexts.tabs.propertyVisit')}
            />
            <Tab
              value="documents"
              label={
                <Tooltip title={tabsDisabledTooltip} arrow classes={{ tooltip: 'tooltip' }}>
                  <div className="w-100 h-100">{t('propertyVisits.addEditView.pageTexts.tabs.visitDocuments')}</div>
                </Tooltip>
              }
              disabled={controller.tabsDisabled}
              classes={{ disabled: 'pe-auto' }}
            />
            <Tab
              value="tasks"
              label={
                <Tooltip title={tabsDisabledTooltip} arrow classes={{ tooltip: 'tooltip' }}>
                  <div className="w-100 h-100 d-flex align-items-center justify-content-center gap-2">
                    {t('propertyVisits.addEditView.pageTexts.tabs.tasks')}
                    {!controller.tabsDisabled && controller.pendingTasksCount > 0 && (
                      <span className="badge bg-warning rounded-pill" style={{ fontSize: 12, minWidth: 22, padding: '3px 6px' }}>
                        {controller.pendingTasksCount}
                      </span>
                    )}
                  </div>
                </Tooltip>
              }
              disabled={controller.tabsDisabled}
              classes={{ disabled: 'pe-auto' }}
            />
          </Tabs>
        </div>

        <hr className="m-0" />

        {/* Tab 1: Property Visit */}
        {controller.activeTab === 'visit' && (
          <VisitTab
            formData={controller.visitFormData}
            validation={controller.visitValidation}
            isEditable={controller.isFormEditable}
            isAdmin={controller.isAdmin}
            isInspector={controller.isInspector}
            internalNoteRequired={controller.internalNoteRequired}
            isAddMode={controller.isAddMode}
            dateMinDate={controller.dateMinDate}
            dateMaxDate={controller.dateMaxDate}
            allProperties={(controller.allProperties as any) || []}
            properties={(controller.properties as any) || []}
            allVisitTypes={(controller.allVisitTypes as any) || []}
            visitTypes={(controller.visitTypes as any) || []}
            allUserRoles={(controller.allUserRoles as any) || []}
            userRoles={(controller.userRoles as any) || []}
            allVisitedByOptions={(controller.allVisitedByOptions as any) || []}
            visitedByOptions={(controller.visitedByOptions as any) || []}
            allVisitStatuses={(controller.allVisitStatuses as any) || []}
            dropdownSearchData={controller.dropdownSearchData}
            activeGuest={controller.activeGuest}
            loading={controller.visitSaveLoading}
            onInputChange={controller.handleVisitInputChange}
            onInputBlur={controller.handleVisitInputBlur}
            onDropdownSearchChange={controller.handleDropdownSearchChange}
            onSave={controller.handleSaveVisit}
            onCancel={controller.handleCancel}
          />
        )}

        {/* Tab 2: Visit Documents */}
        {controller.activeTab === 'documents' && (
          <DocumentsTab
            reportUrl={controller.reportUrl}
            reportUrlError={controller.reportUrlError}
            onReportUrlChange={controller.handleReportUrlChange}
            existingDocs={(controller.existingDocs as any) || []}
            onRemoveExistingDoc={controller.handleRemoveExistingDoc}
            pendingPdfFile={controller.pendingPdfFile}
            pendingImageFiles={(controller.pendingImageFiles as any) || []}
            onAddPdfFile={controller.handleAddPdfFile}
            onRemovePendingPdf={controller.handleRemovePendingPdf}
            onAddImageFiles={controller.handleAddImageFiles}
            onRemovePendingImage={controller.handleRemovePendingImage}
            isEditable={controller.isFormEditable}
            loading={controller.docLoading}
            onSave={controller.handleSaveDocuments}
            onCancel={controller.handleCancel}
          />
        )}

        {/* Tab 3: Tasks */}
        {controller.activeTab === 'tasks' && (
          <TasksTab
            tasks={controller.tasks}
            isEditable={controller.isFormEditable}
            taskModalOpen={controller.taskModalOpen}
            taskDeleteModalOpen={controller.taskDeleteModalOpen}
            editingTask={controller.editingTask}
            taskFormData={controller.taskFormData}
            taskValidation={controller.taskValidation}
            taskLoading={controller.taskLoading}
            taskDropdownSearchData={controller.taskDropdownSearchData}
            allCategoryOptions={(controller.allCategoryOptions as any) || []}
            categoryOptions={(controller.categoryOptions as any) || []}
            allPriorityOptions={(controller.allPriorityOptions as any) || []}
            priorityOptions={(controller.priorityOptions as any) || []}
            allStatusOptions={(controller.allStatusOptions as any) || []}
            statusOptions={(controller.statusOptions as any) || []}
            onOpenAddTask={controller.handleOpenAddTask}
            onOpenEditTask={controller.handleOpenEditTask}
            onCloseTaskModal={controller.handleCloseTaskModal}
            onTaskFieldChange={controller.handleTaskFieldChange}
            onTaskFieldBlur={controller.handleTaskFieldBlur}
            onTaskDropdownSearchChange={controller.handleTaskDropdownSearchChange}
            onSaveTask={controller.handleSaveTask}
            onOpenDeleteTask={controller.handleOpenDeleteTask}
            onCloseDeleteTask={controller.handleCloseDeleteTask}
            onConfirmDeleteTask={controller.handleConfirmDeleteTask}
          />
        )}
      </div>
    </>
  );
};

export default AddEditView;
